import React, { useState } from "react";
import { useGetBookingsQuery, useUpdateBookingStatusMutation, useGetAvailableRoomsMutation } from "../redux/api/roomBookingApi";

export default function BookingList() {
  const { data, isLoading, refetch } = useGetBookingsQuery();
  const [updateStatus] = useUpdateBookingStatusMutation();
  const [getAvailableRooms] = useGetAvailableRoomsMutation();

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState([]); // Array instead of string
  const [isFetchingRooms, setIsFetchingRooms] = useState(false);

  if (isLoading) return <p>Loading...</p>;

  // Triggered when Status dropdown changes
  const handleStatusChange = async (booking, newStatus) => {
    // If Checking In -> Open Modal
    if (newStatus === 'checked_in') {
      setCurrentBooking(booking);
      setIsModalOpen(true);
      setAvailableRooms([]);
      // Initialize selectedRooms array based on no_of_rooms or 1
      const noOfRooms = booking.no_of_rooms || 1;
      setSelectedRooms(Array(noOfRooms).fill(""));
      setIsFetchingRooms(true);

      try {
        const res = await getAvailableRooms(booking.building_id).unwrap();
        if (res.success && res.data) {
          setAvailableRooms(res.data);
          if (res.data.length > 0) {
            // Default select the first N distinct available rooms if possible
            const defaultSelections = [];
            for (let i = 0; i < noOfRooms; i++) {
              defaultSelections.push(res.data[i] || res.data[0]);
            }
            setSelectedRooms(defaultSelections);
          }
        }
      } catch (err) {
        console.error("Failed to fetch rooms", err);
        alert("Error fetching available rooms: " + (err.data?.message || err.message));
      } finally {
        setIsFetchingRooms(false);
      }
      return;
    }

    // For other statuses (e.g. Confirm, Check-out), update immediately
    updateBookingStatus(booking._id, newStatus);
  };

  const updateBookingStatus = async (id, status, roomNosArray = null) => {
    try {
      let payload = { id, status };
      if (roomNosArray) payload.room_nos = roomNosArray; // Send array of room nos

      await updateStatus(payload).unwrap();
      refetch();
      setIsModalOpen(false); // Close modal if open
    } catch (err) {
      console.error(err);
      alert("Failed to update: " + (err.data?.message || err.message));
    }
  }

  const handleModalConfirm = () => {
    // Validate that all dropdowns have a room selected
    if (selectedRooms.some(r => !r || r.trim() === "")) {
      alert("Please select a room for all slots");
      return;
    }
    // Check for duplicates
    const uniqueSelections = new Set(selectedRooms);
    if (uniqueSelections.size !== selectedRooms.length) {
      alert("Duplicate rooms selected. Please choose distinct rooms.");
      return;
    }
    
    updateBookingStatus(currentBooking._id, 'checked_in', selectedRooms);
  };

  const getStatusColor = (status) => {
    const s = Number(status);
    switch (s) {
      case 1: return "#1976d2"; // Blue (Confirmed)
      case 3: return "#2e7d32"; // Green (Checked In)
      case 2: return "#d32f2f"; // Red (Checked Out)
      default: return "#f0f0f0";
    }
  };

  const getStatusLabel = (status) => {
    const s = Number(status);
    switch (s) {
      case 1: return "Confirmed";
      case 3: return "Checked In";
      case 2: return "Checked Out";
      default: return "Pending";
    }
  }

  return (
    <div>
      <h2>Room Bookings</h2>

      <table border="1" cellPadding="8" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#eee' }}>
            <th>Booking ID</th>
            <th>Name</th>
            <th>Phone</th>
            <th>Building</th>
            <th>Room</th>
            <th>Rent</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          {data?.data?.map((item) => (
            <tr key={item._id} style={{ background: getStatusColor(item.status) === '#f0f0f0' ? 'inherit' : `${getStatusColor(item.status)}20` }}>
              <td>{item.booking_id}</td>
              <td>{item.name || (item.user_id && item.user_id.full_name) || "User"}</td>
              <td>{item.phone_num || (item.user_id && item.user_id.phone_num)}</td>
              <td>{item.building_id}</td>
              <td>
                {item.allocated_room_no || (item.room_id?.length ? item.room_id.map(r => r.room_no).join(", ") : "N/A")}
              </td>
              <td>{item.room_rent || item.paymentCost || item.payment_amount}</td>
              <td>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  color: item.status === 1 || item.status === 2 || item.status === 3 ? 'white' : 'black',
                  backgroundColor: getStatusColor(item.status)
                }}>
                  {getStatusLabel(item.status)}
                </span>
              </td>
              <td>
                <select
                  value={getStatusLabel(item.status).toLowerCase().replace(' ', '_')}
                  onChange={(e) => handleStatusChange(item, e.target.value)}
                  style={{ padding: '5px' }}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirm</option>
                  <option value="checked_in">Check-In</option>
                  <option value="checked_out">Check-Out</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Room Selection Modal */}
      {isModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div style={{ background: 'white', padding: '20px', borderRadius: '8px', minWidth: '300px' }}>
            <h3>Assign Room</h3>
            <p>Booking ID: {currentBooking?.booking_id}</p>

            {isFetchingRooms ? (
              <p>Fetching available rooms...</p>
            ) : availableRooms.length === 0 ? (
              <p style={{ color: 'red' }}>No rooms available in this building!</p>
            ) : (
              <div style={{ margin: '15px 0' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Select Room(s): </label>
                {selectedRooms.map((roomId, index) => (
                  <div key={`room-select-${index}`} style={{ marginBottom: '8px' }}>
                    <span style={{ marginRight: '8px', fontSize: '14px', fontWeight: 'bold' }}>Room {index + 1}:</span>
                    <select
                      value={roomId}
                      onChange={(e) => {
                        const newRooms = [...selectedRooms];
                        newRooms[index] = e.target.value;
                        setSelectedRooms(newRooms);
                      }}
                      style={{ padding: '8px', width: '120px' }}
                    >
                      <option value="">Select</option>
                      {availableRooms.map(r => (
                        <option key={r} value={r} disabled={selectedRooms.includes(r) && r !== roomId}>{r}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{ padding: '8px 16px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleModalConfirm}
                disabled={availableRooms.length === 0 || isFetchingRooms}
                style={{
                  padding: '8px 16px', cursor: 'pointer',
                  background: '#1976d2', color: 'white', border: 'none', borderRadius: '4px',
                  opacity: (availableRooms.length === 0 || isFetchingRooms) ? 0.5 : 1
                }}
              >
                Confirm Allocation
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
